import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { BarChart3, Star, Trophy, Gamepad2, Loader2, ArrowRight, TrendingUp } from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from "recharts";

const GAME_LABELS: Record<string, { name: string; emoji: string; color: string }> = {
  letters: { name: "الحروف", emoji: "🔤", color: "#10b981" },
  numbers: { name: "الأرقام", emoji: "🔢", color: "#3b82f6" },
  math: { name: "الحساب", emoji: "🧮", color: "#f59e0b" },
  words: { name: "الكلمات", emoji: "📝", color: "#06b6d4" },
  memory: { name: "الذاكرة", emoji: "🧠", color: "#8b5cf6" },
  shapes: { name: "الأشكال", emoji: "🎨", color: "#ef4444" },
};

const PIE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#06b6d4", "#8b5cf6", "#ef4444"];

const GameReports = () => {
  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const { profile } = useAuth();
  const navigate = useNavigate();
  const nurseryId = profile?.nursery_id;

  const { data: children } = useQuery({
    queryKey: ["children-for-reports", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("children").select("id, full_name").eq("nursery_id", nurseryId!).order("full_name");
      return data || [];
    },
    enabled: !!nurseryId,
  });

  const { data: results, isLoading } = useQuery({
    queryKey: ["game-results", selectedChildId],
    queryFn: async () => {
      const { data } = await supabase
        .from("game_results")
        .select("*")
        .eq("child_id", selectedChildId)
        .order("played_at", { ascending: false });
      return data || [];
    },
    enabled: !!selectedChildId,
  });

  // Stats calculations
  const totalGames = results?.length || 0;
  const avgPercentage = totalGames > 0 ? Math.round((results!.reduce((a, r) => a + (r.percentage || 0), 0)) / totalGames) : 0;
  const avgStars = totalGames > 0 ? (results!.reduce((a, r) => a + (r.stars || 0), 0) / totalGames).toFixed(1) : "0";
  const totalScore = results?.reduce((a, r) => a + (r.score || 0), 0) || 0;

  // Per-game stats for bar chart
  const gameStats = Object.keys(GAME_LABELS).map((type) => {
    const gameResults = (results || []).filter((r) => r.game_type === type);
    const count = gameResults.length;
    const avg = count > 0 ? Math.round(gameResults.reduce((a, r) => a + (r.percentage || 0), 0) / count) : 0;
    return { type, name: GAME_LABELS[type].name, emoji: GAME_LABELS[type].emoji, count, avg, color: GAME_LABELS[type].color };
  }).filter((g) => g.count > 0);

  // Timeline data (last 10 games)
  const timelineData = (results || []).slice(0, 10).reverse().map((r, i) => ({
    name: `#${i + 1}`,
    percentage: r.percentage || 0,
    game: GAME_LABELS[r.game_type]?.emoji || "🎮",
  }));

  // Pie data
  const pieData = gameStats.map((g) => ({ name: g.name, value: g.count, color: g.color }));

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-primary" />
            تقارير أداء الألعاب
          </h1>
          <p className="text-muted-foreground text-sm mt-1">تتبع تقدم الأطفال في الألعاب التعليمية</p>
        </div>
        <Button variant="outline" onClick={() => navigate("/dashboard/interactive-learning")}>
          <Gamepad2 className="w-4 h-4 ml-2" />
          العودة للألعاب
        </Button>
      </div>

      {/* Child Selection */}
      <Card className="shadow-card">
        <CardContent className="p-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Label className="text-sm font-medium whitespace-nowrap">👦 اختر الطفل:</Label>
          <Select value={selectedChildId} onValueChange={setSelectedChildId}>
            <SelectTrigger className="w-full sm:w-[250px]">
              <SelectValue placeholder="اختر طفلاً لعرض تقاريره" />
            </SelectTrigger>
            <SelectContent>
              {(children || []).map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {!selectedChildId ? (
        <div className="text-center py-16 text-muted-foreground">
          <Trophy className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>اختر طفلاً لعرض تقارير أدائه</p>
        </div>
      ) : isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>
      ) : totalGames === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Gamepad2 className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p>لا توجد نتائج بعد. ابدأ اللعب لتسجيل النتائج!</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "إجمالي الألعاب", value: totalGames, icon: "🎮", color: "from-emerald-400 to-teal-500" },
              { label: "متوسط الأداء", value: `${avgPercentage}%`, icon: "📊", color: "from-blue-400 to-indigo-500" },
              { label: "متوسط النجوم", value: avgStars, icon: "⭐", color: "from-yellow-400 to-orange-500" },
              { label: "إجمالي النقاط", value: totalScore, icon: "🏆", color: "from-purple-400 to-pink-500" },
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}>
                <Card className="shadow-card overflow-hidden">
                  <CardContent className="p-0">
                    <div className={`bg-gradient-to-br ${stat.color} p-4 text-white text-center`}>
                      <p className="text-2xl mb-1">{stat.icon}</p>
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs opacity-80">{stat.label}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Per-game performance */}
            <Card className="shadow-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  متوسط الأداء حسب اللعبة
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={gameStats} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" domain={[0, 100]} />
                    <YAxis type="category" dataKey="name" width={60} tick={{ fontSize: 12 }} />
                    <Tooltip formatter={(v: number) => [`${v}%`, "متوسط"]} />
                    <Bar dataKey="avg" radius={[0, 6, 6, 0]}>
                      {gameStats.map((g, i) => (
                        <Cell key={i} fill={g.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Games distribution pie */}
            <Card className="shadow-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Gamepad2 className="w-4 h-4 text-primary" />
                  توزيع الألعاب
                </h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, value }) => `${name} (${value})`}>
                      {pieData.map((_, i) => (
                        <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Progress Timeline */}
          {timelineData.length > 1 && (
            <Card className="shadow-card">
              <CardContent className="p-4">
                <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  تطور الأداء (آخر 10 ألعاب)
                </h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(v: number) => [`${v}%`, "النسبة"]} />
                    <Line type="monotone" dataKey="percentage" stroke="hsl(160, 55%, 38%)" strokeWidth={2} dot={{ fill: "hsl(160, 55%, 38%)", r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Recent Results Table */}
          <Card className="shadow-card">
            <CardContent className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-4">📋 آخر النتائج</h3>
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {(results || []).slice(0, 20).map((r, i) => {
                  const game = GAME_LABELS[r.game_type] || { name: r.game_type, emoji: "🎮" };
                  return (
                    <motion.div
                      key={r.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.03 }}
                      className="flex items-center gap-3 bg-muted/30 rounded-lg p-3 text-sm"
                    >
                      <span className="text-2xl">{game.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-foreground">{game.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(r.played_at), "d MMM yyyy - HH:mm", { locale: ar })}
                          {r.duration_seconds && ` • ${r.duration_seconds} ثانية`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge variant={r.percentage && r.percentage >= 70 ? "default" : "secondary"} className="text-xs">
                          {r.percentage}%
                        </Badge>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, si) => (
                            <Star key={si} className={`w-3.5 h-3.5 ${si < (r.stars || 0) ? "text-yellow-400 fill-yellow-400" : "text-muted"}`} />
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground font-mono">{r.score}/{r.max_score}</span>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default GameReports;
